/*
 * Guard against the React-17 + in-browser-translation crash.
 *
 * When a browser's page translator (Google Translate / iOS Safari Translate)
 * rewrites text nodes, it re-parents them under injected <font> wrappers. React
 * still holds references to the original nodes, so on the next commit it calls
 * removeChild/insertBefore against a parent that no longer owns the node. In
 * React 17 this manifests, on mobile in particular, as an unrecoverable
 * "RangeError: Maximum call stack size exceeded" — which is exactly the
 * top real bug in this project's tracker, seen on the /submitted (thank-you)
 * pages while a user was translating the page (e.g. Dutch → Turkish).
 *
 * This is the well-established, translation-preserving mitigation (see
 * facebook/react#11538): make removeChild/insertBefore no-op when the node's
 * parent has already changed, instead of throwing/recursing. Users keep the
 * accessibility benefit of translation; React stops crashing.
 *
 * Kept out of a render path on purpose — it patches the DOM prototype once at
 * startup and must run before React mounts.
 */
export const installDomTranslationGuard = () => {
  if (typeof Node !== "function" || !Node.prototype) {
    return;
  }

  const originalRemoveChild = Node.prototype.removeChild;
  Node.prototype.removeChild = function removeChild(child) {
    if (child.parentNode !== this) {
      // eslint-disable-next-line no-console
      if (console) console.error("Cannot remove a child from a different parent", child, this);
      return child;
    }
    // eslint-disable-next-line prefer-rest-params
    return originalRemoveChild.apply(this, arguments);
  };

  const originalInsertBefore = Node.prototype.insertBefore;
  Node.prototype.insertBefore = function insertBefore(newNode, referenceNode) {
    if (referenceNode && referenceNode.parentNode !== this) {
      // eslint-disable-next-line no-console
      if (console) console.error("Cannot insert before a reference node from a different parent", referenceNode, this);
      return newNode;
    }
    // eslint-disable-next-line prefer-rest-params
    return originalInsertBefore.apply(this, arguments);
  };
};
